"use client";

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import { DockSheet } from "@/components/ui/dock-sheet";
import { SearchablePickerList } from "@/components/ui/searchable-picker-list";
import { SHEET_Z_PICKER } from "@/lib/sheet-styles";
import {
  DEFAULT_PHONE_COUNTRY,
  fuzzyMatchPhoneCountry,
  getPhoneCountries,
  getPhoneCountryByDial,
  type PhoneCountry,
} from "@/lib/countries";
import type { SearchableOption } from "@/lib/dropdown-types";

export type Option = SearchableOption;

export type { PhoneCountry as Country };

interface PhoneInputProps {
  defaultValue?: string;
  value?: string;
  countryCode?: string;
  name?: string;
  placeholder?: string;
  onChange?: (value: string, country: PhoneCountry) => void;
  className?: string;
  /**
   * Which field dialect to render: the on-panel one (`--sheet*` surface, handle
   * border, muted-foreground ring) or the page one (`bg-background`,
   * `border-border`, `ring-ring`). Both are theme-dependent tokens.
   *
   * WAS `dark`. That name stopped being true in step 2d, when this component's
   * branch moved off `neutral-*` literals onto tokens — the branch no longer
   * forces a colour, it picks a surface. Genuinely two-valued: the checkout
   * shipping form passes `true`, the auth phone step and the address book pass
   * nothing.
   * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md
   */
  onSheet?: boolean;
  deferKeyboardFocus?: boolean;
  onUnlockKeyboardFocus?: () => void;
}

function phoneCountryToOption(country: PhoneCountry): Option {
  return {
    code: country.isoCode,
    name: country.name,
    flag: country.flag,
    isoCode: country.isoCode,
    trailing: country.code,
  };
}

export default function PhoneInput({
  defaultValue = "",
  value,
  countryCode,
  name = "phoneNumber",
  placeholder = "Enter your phone number",
  onChange,
  className = "",
  onSheet = false,
  deferKeyboardFocus = false,
  onUnlockKeyboardFocus,
}: PhoneInputProps) {
  const isMobile = useMobile();
  const countries = useMemo(() => getPhoneCountries(), []);
  const countryOptions = useMemo(() => countries.map(phoneCountryToOption), [countries]);

  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(
    getPhoneCountryByDial(countryCode) || DEFAULT_PHONE_COUNTRY,
  );
  const [phoneNumber, setPhoneNumber] = useState(value ?? defaultValue);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!countryCode) return;
    const next = getPhoneCountryByDial(countryCode);
    if (next) {
      setSelectedCountry((prev) => (prev.isoCode === next.isoCode ? prev : next));
    }
  }, [countryCode]);

  useEffect(() => {
    if (typeof value === "string") {
      setPhoneNumber(value);
      return;
    }
    if (value === undefined) {
      setPhoneNumber(defaultValue);
    }
  }, [value, defaultValue]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsPickerOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile]);

  const filteredCountries = useMemo(() => {
    if (!query.trim()) return countryOptions;
    return countryOptions.filter((option) =>
      fuzzyMatchPhoneCountry(query, {
        isoCode: option.isoCode ?? option.code,
        code: String(option.trailing ?? ""),
        name: option.name,
        flag: option.flag ?? "",
      }),
    );
  }, [countryOptions, query]);

  const closePicker = useCallback(() => {
    setIsPickerOpen(false);
    setQuery("");
  }, []);

  const handleCountrySelect = (option: Option) => {
    const country =
      countries.find((c) => c.isoCode === option.isoCode || c.isoCode === option.code) ??
      DEFAULT_PHONE_COUNTRY;
    setSelectedCountry(country);
    closePicker();
    onChange?.(phoneNumber, country);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setPhoneNumber(nextValue);
    onChange?.(nextValue, selectedCountry);
  };

  const pickerList = (
    <SearchablePickerList
      options={filteredCountries}
      label="name"
      query={query}
      onQueryChange={setQuery}
      onSelect={handleCountrySelect}
      showFlag
      showCode
      searchPlaceholder="Search countries..."
      emptyMessage="No countries found"
      selectedCode={selectedCountry.isoCode}
      clientFilter={false}
      variant={isMobile ? "sheet" : "dropdown"}
      listClassName={isMobile ? "flex-1" : undefined}
      listStyle={isMobile ? { maxHeight: "100%" } : { maxHeight: "min(200px, 36vh)" }}
    />
  );

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={cn(
          "flex items-center relative rounded-full border overflow-hidden h-12 transition focus-within:ring-2 focus-within:ring-offset-2",
          className,
          onSheet
            ? "border-sheet-handle bg-sheet text-foreground focus-within:ring-muted-foreground/70 focus-within:ring-offset-sheet"
            : "border-border bg-background focus-within:ring-ring",
        )}
        style={{ minHeight: 48 }}
      >
        <button
          type="button"
          onClick={() => setIsPickerOpen((prev) => !prev)}
          className={cn("flex items-center gap-2 px-4 py-3 h-12 focus:outline-hidden transition", onSheet && "text-foreground")}
          style={{ minHeight: 48 }}
          aria-label="Select country code"
        >
          <img src={selectedCountry.flag || "/placeholder.svg"} alt="" className="w-5 h-5" />
          <span className="text-base">{selectedCountry.code}</span>
          <ChevronDown className={cn("h-4 w-4 shrink-0", onSheet ? "text-muted-foreground" : "opacity-50")} />
        </button>

        <span className={cn("h-6 w-px mx-1", onSheet ? "bg-foreground/20" : "bg-border")} />

        <input
          type="tel"
          name={name}
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className={cn(
            "w-full h-12 px-4 py-3 text-base bg-transparent border-none focus:outline-hidden",
            onSheet ? "text-foreground placeholder:text-muted-foreground" : "",
          )}
          style={{ fontSize: 16 }}
          autoComplete="tel-national"
          readOnly={deferKeyboardFocus}
          onPointerDown={
            deferKeyboardFocus
              ? (event) => {
                  if (event.currentTarget instanceof HTMLInputElement) {
                    event.currentTarget.readOnly = false;
                  }
                  onUnlockKeyboardFocus?.();
                }
              : undefined
          }
        />
      </div>

      {isMobile ? (
        <DockSheet
          open={isPickerOpen}
          onOpenChange={(open) => {
            if (open) {
              setIsPickerOpen(true);
            } else {
              closePicker();
            }
          }}
          title="Select country"
          zIndex={SHEET_Z_PICKER}
          scrollManagedByChild
          contentClassName="flex min-h-0 flex-col overflow-hidden pt-0"
        >
          <div className="-mx-4 flex min-h-0 flex-1 flex-col">
            {pickerList}
          </div>
        </DockSheet>
      ) : (
        isPickerOpen && (
          /* Desktop picker is absolutely positioned INSIDE `containerRef`, not
             portalled — it stays a descendant of the panel, so `--sheet*`
             resolves against the same subtree and it is NOT a 2f flip point. */
          <div
            className={cn(
              "absolute z-50 mt-1 w-full overflow-hidden rounded-3xl border shadow-lg",
              onSheet
                ? "bg-sheet border-sheet-handle text-foreground"
                : "bg-popover text-popover-foreground",
            )}
            style={{ maxHeight: "min(240px, 40vh)" }}
          >
            {pickerList}
          </div>
        )
      )}
    </div>
  );
}
